/**
 * Page Content Hook
 *
 * TanStack Query wrapper for CMS-managed page section content.
 * Returns null when the section is not yet configured in the Backoffice CMS,
 * so components can fall back to their hardcoded default content.
 *
 * CMS-Ready: When the Backoffice adds a Page Content module, this hook will
 * automatically return real data instead of null — zero frontend changes needed.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchPageSection } from '@/api/pageContent';
import { useLocale } from '@/i18n/useLocale';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Fetch a CMS page section with graceful fallback.
 *
 * Usage:
 * ```tsx
 * const cms = usePageSection<HowItWorksContent>('home', 'how-it-works');
 * const content = cms ?? DEFAULT_HOW_IT_WORKS;
 * ```
 */
export const usePageSection = <T>(
    pageSlug: string,
    sectionKey: string
): T | null => {
    const localeState = useLocale();
    const { data } = useQuery({
        queryKey: queryKeys.pageContent(localeState?.locale ?? 'en', pageSlug, sectionKey),
        queryFn: () => {
            if (!localeState) {
                return Promise.resolve(null);
            }

            return fetchPageSection<T>(localeState.manifest, localeState.locale, pageSlug, sectionKey);
        },
        enabled: localeState !== null,
        retry: false,
        staleTime: 10 * 60 * 1000, // 10 minutes
        refetchOnMount: true,
    });

    return data ?? null;
};
